import React, { useState, useCallback } from 'react';
import { Imovel, TipoImovel, StatusImovel, ModalidadeAluguel, MODALIDADES_OPCOES, Midia } from '../../types/Property';
import { useMyProperties } from '../../hooks/useProperties';
import { apiService } from '../../services/ApiService';
import { getImovelModalidades, sanitizeModalidades } from '../../utils/modalidadeUtils';
import './MultiStepPropertyForm.css';

interface MultiStepPropertyFormProps {
  onClose: () => void;
  onSuccess: () => void;
  editingImovel?: Imovel;
  adminMode?: boolean; // Nova prop para modo administrativo
}

interface PropertyFormData {
  titulo: string;
  descricao: string;
  endereco: string;
  cidade: string;
  estado: string;
  bairro: string;
  cep: string;
  tipo_imovel: TipoImovel;
  valor_aluguel: number | null;
  valor_venda: number | null;
  valor_condominio: number;
  area_util: number;
  quartos: number;
  banheiros: number;
  vagas_garagem: number;
  aceita_pet: boolean;
  mobiliado: boolean;
  ar_condicionado: boolean;
  internet: boolean;
  comodidades: string[]; // Array de comodidades
  modalidade_aluguel?: ModalidadeAluguel; // Mantido para compatibilidade
  modalidades?: string[]; // Array de tipos de modalidade para enviar à API
  status: StatusImovel;
  fotos: File[];
  coverImageIndex?: number; // Índice da imagem de capa
  photoOrder: number[]; // Array com a ordem das fotos [índice na ordem, índice original]
}

const STEPS = [
  { id: 1, title: 'Informações Básicas', description: 'Título e descrição do imóvel' },
  { id: 2, title: 'Localização', description: 'Endereço e localização' },
  { id: 3, title: 'Características', description: 'Tipo e detalhes do imóvel' },
  { id: 4, title: 'Valores', description: 'Preços e taxas' },
  { id: 5, title: 'Fotos', description: 'Adicione fotos do imóvel' }
];

export const MultiStepPropertyForm: React.FC<MultiStepPropertyFormProps> = ({ onClose, onSuccess, editingImovel, adminMode = false }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<PropertyFormData>(() => {
    if (editingImovel) {
      return {
        titulo: editingImovel.titulo || '',
        descricao: editingImovel.descricao || '',
        endereco: editingImovel.endereco || '',
        cidade: editingImovel.cidade || '',
        estado: editingImovel.estado || 'SP',
        bairro: editingImovel.bairro || '',
        cep: editingImovel.cep || '',
        tipo_imovel: editingImovel.tipo_imovel || TipoImovel.APARTAMENTO,
        valor_aluguel: editingImovel.valor_aluguel || null,
        valor_venda: editingImovel.valor_venda || null,
        valor_condominio: editingImovel.valor_condominio || 0,
        area_util: editingImovel.area_util || 0,
        quartos: editingImovel.quartos || 0,
        banheiros: editingImovel.banheiros || 0,
        vagas_garagem: editingImovel.vagas_garagem || 0,
        aceita_pet: editingImovel.aceita_pet || false,
        mobiliado: editingImovel.mobiliado || false,
        ar_condicionado: editingImovel.ar_condicionado || false,
        internet: editingImovel.internet || false,
        comodidades: editingImovel.comodidades || [],
        modalidade_aluguel: editingImovel.modalidade_aluguel,
        modalidades: getImovelModalidades(editingImovel).map(m => m.tipo), // Extrair tipos das modalidades
        status: StatusImovel.PENDENTE, // Sempre volta para pendente ao editar
        fotos: [],
        coverImageIndex: editingImovel.midias && editingImovel.midias.length > 0 ? 0 : undefined,
        photoOrder: editingImovel.midias ? editingImovel.midias.map((_, index) => index) : []
      };
    }
    
    return {
      titulo: '',
      descricao: '',
      endereco: '',
      cidade: '',
      estado: 'SP',
      bairro: '',
      cep: '',
      tipo_imovel: TipoImovel.APARTAMENTO,
      valor_aluguel: null,
      valor_venda: null,
      valor_condominio: 0,
      area_util: 0,
      quartos: 0,
      banheiros: 0,
      vagas_garagem: 0,
      aceita_pet: false,
      mobiliado: false,
      ar_condicionado: false,
      internet: false,
      comodidades: [],
      modalidade_aluguel: undefined,
      modalidades: [],
      status: StatusImovel.PENDENTE,
      fotos: [],
      coverImageIndex: undefined,
      photoOrder: []
    };
  });

  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createProperty, updateProperty, uploadPropertyPhotos } = useMyProperties();

  // Função para construir URL da imagem usando o mesmo padrão da área administrativa
  const getImageUrl = React.useCallback((midiaUrl: string) => {
    if (!midiaUrl) return '';

    // Determinar a base da API a partir das envs
    const rawBackend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '') || 'http://localhost:3005';
    const rawApiBaseEnv = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
    const apiBase = rawApiBaseEnv || (rawBackend.endsWith('/api') ? rawBackend : `${rawBackend}/api`);

    // Extrair origin e path da base da API
    let origin = '';
    let apiPath = '/api';
    try {
      const u = new URL(apiBase);
      origin = u.origin;
      apiPath = u.pathname.replace(/\/+$/, '') || '/api';
    } catch {
      origin = '';
      apiPath = '/api';
    }

    // Normalizar caminho de mídia para sempre usar /uploads a partir do início
    const rawPath = midiaUrl.startsWith('/') ? midiaUrl : `/${midiaUrl}`;
    const idx = rawPath.toLowerCase().indexOf('/uploads');
    const uploadsPath = idx >= 0 ? rawPath.substring(idx) : rawPath;
    const cleanPath = uploadsPath.replace(/^(\/+)(api\/)+/i, '/');

    const finalUrl = `${origin}${apiPath}${cleanPath}`;
    return finalUrl;
  }, []);

  // Carregar fotos existentes quando editando
  React.useEffect(() => {
    if (editingImovel) {
      if (editingImovel.midias && editingImovel.midias.length > 0) {
        setExistingPhotos(editingImovel.midias);
        
        // Usar a função síncrona getImageUrl para resolver URLs de imagem
        const previews = editingImovel.midias.map((midia) => {
          const imageUrl = getImageUrl(midia.url);
          return imageUrl;
        });
        
        setPhotoPreviews(previews);
      } else {
        setExistingPhotos([]);
        setPhotoPreviews([]);
      }
    } else {
      setExistingPhotos([]);
      setPhotoPreviews([]);
    }
  }, [editingImovel, getImageUrl]);

  const handleInputChange = (field: keyof PropertyFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Função para buscar endereço por CEP usando ViaCEP
  const buscarEnderecoPorCEP = async (cep: string) => {
    // Remove caracteres não numéricos
    const cepLimpo = cep.replace(/\D/g, '');
    
    // Verifica se o CEP tem 8 dígitos
    if (cepLimpo.length !== 8) {
      return;
    }

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (!data.erro) {
        // Preenche automaticamente os campos de endereço
        setFormData(prev => ({
          ...prev,
          endereco: data.logradouro || '',
          cidade: data.localidade || '',
          estado: data.uf || '',
          bairro: data.bairro || ''
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    }
  };

  // Função para formatar CEP
  const formatarCEP = (cep: string) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length <= 5) {
      return cepLimpo;
    }
    return cepLimpo.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  // Handler específico para CEP com formatação e busca automática
  const handleCEPChange = (value: string) => {
    const cepFormatado = formatarCEP(value);
    handleInputChange('cep', cepFormatado);
    
    // Busca endereço quando CEP estiver completo
    if (cepFormatado.length === 9) { // 00000-000
      buscarEnderecoPorCEP(cepFormatado);
    }
  };

  const handlePhotoUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newPhotos = Array.from(files);
    
    // Atualizar fotos no formData
    setFormData(prev => ({
      ...prev,
      fotos: [...prev.fotos, ...newPhotos]
    }));

    // Criar previews das novas fotos
    newPhotos.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setPhotoPreviews(prev => [...prev, e.target!.result as string]);
        }
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const setCoverImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      coverImageIndex: index
    }));
  };

  const movePhoto = (fromIndex: number, toIndex: number) => {
    setPhotoPreviews(prev => {
      const newPreviews = [...prev];
      const [movedItem] = newPreviews.splice(fromIndex, 1);
      newPreviews.splice(toIndex, 0, movedItem);
      return newPreviews;
    });

    // Movimentar as fotos existentes também
    setExistingPhotos(prev => {
      if (fromIndex < prev.length && toIndex < prev.length) {
        const newPhotos = [...prev];
        const [movedItem] = newPhotos.splice(fromIndex, 1);
        newPhotos.splice(toIndex, 0, movedItem);
        return newPhotos;
      }
      return prev;
    });

    // Ajustar coverImageIndex se necessário
    setFormData(prev => {
      let newCoverIndex = prev.coverImageIndex;
      if (prev.coverImageIndex !== undefined) {
        if (prev.coverImageIndex === fromIndex) {
          newCoverIndex = toIndex;
        } else if (prev.coverImageIndex > fromIndex && prev.coverImageIndex <= toIndex) {
          newCoverIndex = prev.coverImageIndex - 1;
        } else if (prev.coverImageIndex < fromIndex && prev.coverImageIndex >= toIndex) {
          newCoverIndex = prev.coverImageIndex + 1;
        }
      }
      
      return {
        ...prev,
        coverImageIndex: newCoverIndex
      };
    });
  };

  const [draggedPhotoIndex, setDraggedPhotoIndex] = useState<number | null>(null);

  const removePhoto = (index: number) => {
    // Se estamos removendo uma foto existente
    if (index < existingPhotos.length) {
      // Marcar foto existente para remoção
      setExistingPhotos(prev => prev.map((photo, i) => 
        i === index ? { ...photo, toRemove: true } : photo
      ));
    } else {
      // Se estamos removendo uma foto nova (uploaded)
      const newPhotoIndex = index - existingPhotos.length;
      setFormData(prev => ({
        ...prev,
        fotos: prev.fotos.filter((_, i) => i !== newPhotoIndex)
      }));
      
      // Remover do photoPreviews também
      setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
    }
    
    // Ajustar o índice da capa se necessário
    if (formData.coverImageIndex === index) {
      setFormData(prev => ({
        ...prev,
        coverImageIndex: undefined
      }));
    } else if (formData.coverImageIndex !== undefined && formData.coverImageIndex > index) {
      setFormData(prev => ({
        ...prev,
        coverImageIndex: prev.coverImageIndex! - 1
      }));
    }
  };

  const nextStep = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    console.log('🚀 handleSubmit iniciado');
    console.log('📋 editingImovel:', editingImovel);
    console.log('📝 formData:', formData);
    
    setIsSubmitting(true);
    
    try {
      // Validar modalidades antes de enviar
      const modalidadesValidadas = formData.modalidades && formData.modalidades.length > 0
        ? sanitizeModalidades(formData.modalidades)
        : [];

      const imovelData: any = {
        titulo: formData.titulo,
        descricao: formData.descricao,
        endereco: formData.endereco,
        cidade: formData.cidade,
        estado: formData.estado,
        bairro: formData.bairro,
        cep: formData.cep,
        tipo_imovel: formData.tipo_imovel,
        valor_aluguel: formData.valor_aluguel,
        valor_venda: formData.valor_venda,
        valor_condominio: formData.valor_condominio,
        area_util: formData.area_util,
        quartos: formData.quartos,
        banheiros: formData.banheiros,
        vagas_garagem: formData.vagas_garagem,
        aceita_pet: formData.aceita_pet,
        mobiliado: formData.mobiliado,
        ar_condicionado: formData.ar_condicionado,
        internet: formData.internet,
        comodidades: formData.comodidades || [],
        status: formData.status,
        // Enviar modalidades como array de strings (tipos)
        modalidades: modalidadesValidadas
      };

      console.log('📦 imovelData preparado:', imovelData);

      let result;
      let imovelId: string;

      if (editingImovel) {
        // EDITAR imóvel existente
        console.log('🔄 Editando imóvel existente:', editingImovel.id);
        result = await updateProperty(editingImovel.id, imovelData);
        imovelId = editingImovel.id;
        console.log('✅ Resultado da edição:', result);
      } else {
        // CRIAR novo imóvel
        console.log('➕ Criando novo imóvel');
        result = await createProperty(imovelData);
        imovelId = result.data?.id;
        console.log('✅ Resultado da criação:', result);
      }

      if (result.success) {
        console.log('🎉 Operação bem-sucedida!');
        
        // Remover fotos marcadas para remoção
        const photosToRemove = existingPhotos.filter(photo => photo.toRemove);
        if (photosToRemove.length > 0 && imovelId) {
          console.log('🗑️ Removendo fotos marcadas para exclusão:', photosToRemove.length);
          for (const photo of photosToRemove) {
            try {
              const deleteResult = await apiService.deleteMidia(photo.id);
              if (deleteResult.success) {
                console.log('✅ Foto removida:', photo.id);
              } else {
                console.error('❌ Erro ao remover foto:', photo.id, deleteResult.message);
              }
            } catch (error) {
              console.error('❌ Erro ao remover foto:', photo.id, error);
            }
          }
        }
        
        // Upload das fotos novas se houver e capturar IDs retornados
        let uploadedMidias: any[] = [];
        if (formData.fotos.length > 0 && imovelId) {
          console.log('📸 Fazendo upload de fotos para imóvel:', imovelId);
          const uploadResult = await uploadPropertyPhotos(imovelId, formData.fotos);
          if (!uploadResult.success) {
            console.error('Erro ao fazer upload das fotos:', uploadResult.message);
            // Não falha a operação por causa do upload
          } else {
            console.log('✅ Upload de fotos realizado com sucesso');
            // Capturar IDs das mídias recém-criadas (retornadas na mesma ordem do upload)
            if (uploadResult.data && Array.isArray(uploadResult.data)) {
              uploadedMidias = uploadResult.data;
              console.log('📋 Mídias criadas:', uploadedMidias.length);
            }
          }
        }

        // Buscar todas as mídias do imóvel para atualizar a ordem
        if (imovelId) {
          console.log('🔄 Buscando todas as mídias do imóvel para atualizar ordem...');
          const midiasResponse = await apiService.getMidias(imovelId);
          
          if (midiasResponse.success && midiasResponse.data) {
            const allMidias = Array.isArray(midiasResponse.data) 
              ? midiasResponse.data 
              : (midiasResponse.data.midias || []);
            
            console.log('📋 Total de mídias encontradas:', allMidias.length);
            
            // Construir mapeamento da ordem visual (photoPreviews) para as mídias reais
            // photoPreviews contém: fotos existentes (não removidas) + novas fotos na ordem visual
            const existingPhotosNotRemoved = existingPhotos.filter(p => !p.toRemove);
            
            // Criar um mapa de URLs para IDs de mídias existentes (para mapear corretamente após reordenação)
            const existingPhotosMap = new Map<string, string>();
            existingPhotosNotRemoved.forEach(photo => {
              if (photo.url) {
                const imageUrl = getImageUrl(photo.url);
                existingPhotosMap.set(imageUrl, photo.id);
              }
            });
            
            // Criar um array com a ordem final das mídias baseado em photoPreviews
            const finalOrder: Array<{ midiaId: string; ordem: number; isCapa: boolean }> = [];
            
            // Percorrer photoPreviews na ordem visual definida pelo usuário
            photoPreviews.forEach((preview, visualIndex) => {
              // Tentar encontrar se é uma foto existente pela URL
              const existingPhotoId = existingPhotosMap.get(preview);
              
              if (existingPhotoId) {
                // É uma foto existente (identificada pela URL)
                finalOrder.push({
                  midiaId: existingPhotoId,
                  ordem: visualIndex + 1,
                  isCapa: formData.coverImageIndex === visualIndex
                });
              } else {
                // É uma nova foto - usar os IDs retornados pelo upload
                const newPhotoIndex = visualIndex - existingPhotosNotRemoved.length;
                if (newPhotoIndex >= 0 && newPhotoIndex < uploadedMidias.length && uploadedMidias[newPhotoIndex]) {
                  const newMidia = uploadedMidias[newPhotoIndex];
                  finalOrder.push({
                    midiaId: newMidia.id,
                    ordem: visualIndex + 1,
                    isCapa: formData.coverImageIndex === visualIndex
                  });
                } else if (uploadedMidias.length === 0 && newPhotoIndex >= 0 && newPhotoIndex < formData.fotos.length) {
                  // Fallback: se não tivermos os IDs do upload, buscar pelas mídias mais recentes
                  const existingPhotoIds = new Set(existingPhotosNotRemoved.map(ep => ep.id));
                  const newMidias = (allMidias as Midia[]).filter((m: Midia) => !existingPhotoIds.has(m.id));
                  
                  // Ordenar por data de criação (mais recentes primeiro) para manter ordem do upload
                  newMidias.sort((a: Midia, b: Midia) => {
                    const dateA = new Date(a.created_at || 0).getTime();
                    const dateB = new Date(b.created_at || 0).getTime();
                    return dateB - dateA;
                  });
                  
                  if (newPhotoIndex < newMidias.length) {
                    const newMidia = newMidias[newPhotoIndex];
                    finalOrder.push({
                      midiaId: newMidia.id,
                      ordem: visualIndex + 1,
                      isCapa: formData.coverImageIndex === visualIndex
                    });
                  }
                }
              }
            });
            
            // Atualizar ordem e capa de todas as mídias
            console.log('🔄 Atualizando ordem e capa de todas as mídias...', finalOrder.length, 'mídias');
            for (const item of finalOrder) {
              try {
                const updateResult = await apiService.updateMidia(item.midiaId, {
                  ordem: item.ordem,
                  is_capa: item.isCapa
                });
                
                if (!updateResult.success) {
                  console.error('❌ Erro ao atualizar mídia:', item.midiaId, updateResult.message);
                } else {
                  console.log(`✅ Mídia ${item.midiaId} atualizada - Ordem: ${item.ordem}, Capa: ${item.isCapa}`);
                }
              } catch (error) {
                console.error('❌ Erro ao atualizar mídia:', item.midiaId, error);
              }
            }
            
            console.log('✅ Ordem e capa de todas as fotos atualizadas com sucesso');
          } else {
            console.warn('⚠️ Não foi possível buscar mídias do imóvel para atualizar ordem');
          }
        }

        console.log('🔄 Chamando onSuccess...');
        onSuccess();
      } else {
        console.error('❌ Erro na operação:', result.message);
        alert('Erro ao salvar imóvel: ' + result.message);
      }
    } catch (error) {
      console.error('❌ Erro ao processar imóvel:', error);
      alert('Erro ao processar imóvel: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.titulo.trim() !== '' && formData.descricao.trim() !== '';
      case 2:
        return formData.endereco.trim() !== '' && formData.cidade.trim() !== '' && formData.bairro.trim() !== '';
      case 3:
        const isComercial = formData.tipo_imovel === TipoImovel.COMERCIAL;
        const isGalpao = formData.tipo_imovel === TipoImovel.GALPAO;
        const quartosValid = (isComercial || isGalpao) ? true : formData.quartos > 0;
        return formData.area_util > 0 && quartosValid && formData.banheiros > 0;
      case 4:
        const checkboxAluguelMarcado = formData.valor_aluguel !== null && formData.valor_aluguel >= 0;
        const checkboxVendaMarcado = formData.valor_venda !== null && formData.valor_venda >= 0;
        
        if (!checkboxAluguelMarcado && !checkboxVendaMarcado) {
          return false;
        }
        
        const valorAluguelValido = !checkboxAluguelMarcado || (formData.valor_aluguel !== null && formData.valor_aluguel > 0);
        const valorVendaValido = !checkboxVendaMarcado || (formData.valor_venda !== null && formData.valor_venda > 0);
        
        return valorAluguelValido && valorVendaValido;
      case 5:
        // Se estiver editando, não exigir fotos novas (pode ter fotos existentes)
        if (editingImovel) {
          return true; // Sempre válido quando editando
        }
        return formData.fotos.length > 0; // Exigir fotos apenas para novos imóveis
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h3>Informações Básicas</h3>
            <div className="form-group">
              <label>Título do Imóvel *</label>
              <input
                type="text"
                value={formData.titulo}
                onChange={(e) => handleInputChange('titulo', e.target.value)}
                placeholder="Ex: Apartamento 3 quartos próximo ao metrô"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Descrição *</label>
              <textarea
                value={formData.descricao}
                onChange={(e) => handleInputChange('descricao', e.target.value)}
                placeholder="Descreva o imóvel, suas características e diferenciais..."
                className="form-textarea"
                rows={4}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h3>Localização</h3>
            <div className="form-group">
              <label>CEP</label>
              <input
                type="text"
                value={formData.cep}
                onChange={(e) => handleCEPChange(e.target.value)}
                placeholder="01234-567"
                className="form-input"
                maxLength={9}
              />
              <small className="form-help">Digite o CEP para buscar automaticamente o endereço</small>
            </div>
            <div className="form-group">
              <label>Endereço Completo *</label>
              <input
                type="text"
                value={formData.endereco}
                onChange={(e) => handleInputChange('endereco', e.target.value)}
                placeholder="Ex: Rua das Flores, 123"
                className="form-input"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Cidade *</label>
                <input
                  type="text"
                  value={formData.cidade}
                  onChange={(e) => handleInputChange('cidade', e.target.value)}
                  placeholder="São Paulo"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Estado *</label>
                <input
                  type="text"
                  value={formData.estado}
                  onChange={(e) => handleInputChange('estado', e.target.value)}
                  placeholder="SP"
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Bairro *</label>
              <input
                type="text"
                value={formData.bairro}
                onChange={(e) => handleInputChange('bairro', e.target.value)}
                placeholder="Vila Madalena"
                className="form-input"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h3>Características do Imóvel</h3>
            <div className="form-group">
              <label>Tipo do Imóvel</label>
              <select
                value={formData.tipo_imovel}
                onChange={(e) => handleInputChange('tipo_imovel', e.target.value as TipoImovel)}
                className="form-select"
              >
                <option value={TipoImovel.APARTAMENTO}>Apartamento</option>
                <option value={TipoImovel.CASA}>Casa</option>
                <option value={TipoImovel.STUDIO}>Studio</option>
                <option value={TipoImovel.LOFT}>Loft</option>
                <option value={TipoImovel.DUPLEX}>Duplex</option>
                <option value={TipoImovel.KITNET}>Kitnet</option>
                <option value={TipoImovel.COBERTURA}>Cobertura</option>
                <option value={TipoImovel.TERRENO}>Terreno</option>
                <option value={TipoImovel.COMERCIAL}>Comercial</option>
                <option value={TipoImovel.GALPAO}>Galpão</option>
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Área Útil (m²) *</label>
                <input
                  type="number"
                  value={formData.area_util}
                  onChange={(e) => handleInputChange('area_util', parseInt(e.target.value) || 0)}
                  placeholder="80"
                  className="form-input"
                />
              </div>
              {formData.tipo_imovel !== TipoImovel.COMERCIAL && formData.tipo_imovel !== TipoImovel.GALPAO && (
                <div className="form-group">
                  <label>Quartos *</label>
                  <input
                    type="number"
                    value={formData.quartos}
                    onChange={(e) => handleInputChange('quartos', parseInt(e.target.value) || 0)}
                    placeholder="3"
                    className="form-input"
                  />
                </div>
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Banheiros *</label>
                <input
                  type="number"
                  value={formData.banheiros}
                  onChange={(e) => handleInputChange('banheiros', parseInt(e.target.value) || 0)}
                  placeholder="2"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Vagas de Garagem</label>
                <input
                  type="number"
                  value={formData.vagas_garagem}
                  onChange={(e) => handleInputChange('vagas_garagem', parseInt(e.target.value) || 0)}
                  placeholder="1"
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Características do Imóvel</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.aceita_pet}
                    onChange={(e) => handleInputChange('aceita_pet', e.target.checked)}
                  />
                  Aceita Pet
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.mobiliado}
                    onChange={(e) => handleInputChange('mobiliado', e.target.checked)}
                  />
                  Mobiliado
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.ar_condicionado}
                    onChange={(e) => handleInputChange('ar_condicionado', e.target.checked)}
                  />
                  Ar Condicionado
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.internet}
                    onChange={(e) => handleInputChange('internet', e.target.checked)}
                  />
                  Internet
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Comodidades do Condomínio</label>
              <p className="form-help">Selecione as comodidades disponíveis no condomínio</p>
              <div className="checkbox-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                {['piscina', 'academia', 'portaria 24h', 'elevador', 'churrasqueira', 'playground', 'salão de festas', 'espaço gourmet', 'quadra', 'lavanderia', 'piscina adulto', 'piscina infantil', 'sauna', 'cinema', 'biblioteca'].map((comodidade) => (
                  <label key={comodidade} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.comodidades.includes(comodidade)}
                      onChange={(e) => {
                        const currentComodidades = formData.comodidades || [];
                        const newComodidades = e.target.checked
                          ? [...currentComodidades, comodidade]
                          : currentComodidades.filter(c => c !== comodidade);
                        handleInputChange('comodidades', newComodidades);
                      }}
                    />
                    <span style={{ textTransform: 'capitalize' }}>{comodidade}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <h3>Valores</h3>
            <div className="form-group">
              <label>Finalidade do Imóvel *</label>
              <p className="form-help">Selecione a finalidade do imóvel. Você pode escolher Aluguel, Venda ou ambos.</p>
              <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={formData.valor_aluguel !== null && formData.valor_aluguel >= 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleInputChange('valor_aluguel', formData.valor_aluguel !== null && formData.valor_aluguel > 0 ? formData.valor_aluguel : 0);
                      } else {
                        handleInputChange('valor_aluguel', null);
                        handleInputChange('modalidades', []);
                      }
                    }}
                  />
                  <span>Disponível para Aluguel</span>
                </label>
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={formData.valor_venda !== null && formData.valor_venda >= 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleInputChange('valor_venda', formData.valor_venda !== null && formData.valor_venda > 0 ? formData.valor_venda : 0);
                      } else {
                        handleInputChange('valor_venda', null);
                      }
                    }}
                  />
                  <span>Disponível para Venda</span>
                </label>
              </div>
            </div>
            
            {(formData.valor_aluguel !== null && formData.valor_aluguel >= 0) && (
              <>
                <div className="form-group">
                  <label>Valor do Aluguel (R$) *</label>
                  <input
                    type="number"
                    value={formData.valor_aluguel !== null ? formData.valor_aluguel : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        handleInputChange('valor_aluguel', 0);
                      } else {
                        const numValue = parseFloat(value);
                        handleInputChange('valor_aluguel', isNaN(numValue) ? 0 : numValue);
                      }
                    }}
                    placeholder="2500.00"
                    className="form-input"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Modalidades de Aluguel</label>
                  <p className="form-help">Selecione uma ou mais modalidades aceitas para este imóvel</p>
                  
                  <div className="modalidades-grid">
                    {MODALIDADES_OPCOES.map((modalidade) => {
                      const modalidadeValueStr = modalidade.value.toString();
                      const isSelected = formData.modalidades?.includes(modalidadeValueStr) || false;
                      
                      return (
                        <div
                          key={modalidade.value}
                          className={`modalidade-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            const currentModalidades = formData.modalidades || [];
                            const newModalidades = isSelected
                              ? currentModalidades.filter(m => m !== modalidadeValueStr)
                              : [...currentModalidades, modalidadeValueStr];
                            
                            setFormData(prev => ({
                              ...prev,
                              modalidades: sanitizeModalidades(newModalidades)
                            }));
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              const currentModalidades = formData.modalidades || [];
                              const newModalidades = isSelected
                                ? currentModalidades.filter(m => m !== modalidadeValueStr)
                                : [...currentModalidades, modalidadeValueStr];
                              
                              setFormData(prev => ({
                                ...prev,
                                modalidades: sanitizeModalidades(newModalidades)
                              }));
                            }}
                            className="checkbox"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="modalidade-info">
                            <strong>{modalidade.label}</strong>
                            <span className="description">{modalidade.description}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Exibir modalidades selecionadas */}
                  {formData.modalidades && formData.modalidades.length > 0 && (
                    <div className="selected-modalidades">
                      <strong>Modalidades selecionadas ({formData.modalidades.length}):</strong>
                      <ul>
                        {formData.modalidades.map(tipo => {
                          const modalidade = MODALIDADES_OPCOES.find(m => m.value === tipo);
                          return <li key={tipo}>{modalidade?.label || tipo}</li>;
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )}

            {(formData.valor_venda !== null && formData.valor_venda >= 0) && (
              <div className="form-group">
                <label>Valor de Venda (R$) *</label>
                <input
                  type="number"
                  value={formData.valor_venda !== null ? formData.valor_venda : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      handleInputChange('valor_venda', 0);
                    } else {
                      const numValue = parseFloat(value);
                      handleInputChange('valor_venda', isNaN(numValue) ? 0 : numValue);
                    }
                  }}
                  placeholder="450000.00"
                  className="form-input"
                  step="0.01"
                  min="0"
                />
              </div>
            )}

            <div className="form-group">
              <label>Valor do Condomínio (R$)</label>
              <input
                type="number"
                value={formData.valor_condominio}
                onChange={(e) => handleInputChange('valor_condominio', parseFloat(e.target.value) || 0)}
                placeholder="400.00"
                className="form-input"
                step="0.01"
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <h3>Fotos do Imóvel</h3>
            <div className="photo-upload-area">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoUpload}
                className="photo-input"
                id="photo-upload"
              />
              <label htmlFor="photo-upload" className="photo-upload-label">
                <div className="upload-icon">📷</div>
                <p>Clique para adicionar fotos</p>
                <span>Ou arraste as imagens aqui</span>
              </label>
            </div>
            
            {photoPreviews.length > 0 && (
              <div className="photo-previews">
                <h4>
                  Fotos do Imóvel ({photoPreviews.length})
                  {existingPhotos.filter(p => !p.toRemove).length > 0 && (
                    <span className="photo-info">
                      - {existingPhotos.filter(p => !p.toRemove).length} existente(s), {formData.fotos.length} nova(s)
                    </span>
                  )}
                </h4>
                <div className="preview-grid">
                  {photoPreviews.map((preview, index) => {
                    // Verificar se esta foto foi marcada para remoção
                    if (index < existingPhotos.length && existingPhotos[index]?.toRemove) {
                      return null;
                    }
                    
                    const isDragged = draggedPhotoIndex === index;
                    return (
                      <div 
                        key={index} 
                        className={`photo-preview ${isDragged ? 'dragging' : ''} ${formData.coverImageIndex === index ? 'is-cover' : ''}`}
                        draggable
                        onDragStart={() => setDraggedPhotoIndex(index)}
                        onDragOver={(e) => {
                          e.preventDefault();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (draggedPhotoIndex !== null && draggedPhotoIndex !== index) {
                            movePhoto(draggedPhotoIndex, index);
                          }
                          setDraggedPhotoIndex(null);
                        }}
                        onDragEnd={() => setDraggedPhotoIndex(null)}
                      >
                        <div className="photo-position">{index + 1}</div>
                        <img 
                          src={preview} 
                          alt={`Preview ${index + 1}`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder-property.jpg';
                          }}
                          onLoad={() => {
                            // Imagem carregada com sucesso
                          }}
                        />
                        <div className="photo-label">
                          {index < existingPhotos.length ? 'Existente' : 'Nova'}
                          {formData.coverImageIndex === index && (
                            <span className="cover-badge">✨ Capa</span>
                          )}
                        </div>
                        <div className="photo-actions">
                          <button 
                            type="button" 
                            className={`set-cover-btn ${formData.coverImageIndex === index ? 'active' : ''}`}
                            onClick={() => setCoverImage(index)}
                            title="Definir como imagem de capa"
                          >
                            {formData.coverImageIndex === index ? '✓ Capa' : 'Definir Capa'}
                          </button>
                          <button 
                            type="button" 
                            className="remove-photo"
                            onClick={() => removePhoto(index)}
                            title="Remover foto"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`multi-step-form-overlay ${adminMode ? 'admin-mode' : ''}`}>
      <div className={`multi-step-form ${adminMode ? 'admin-mode' : ''}`}>
        <div className={`form-header ${adminMode ? 'admin-mode' : ''}`}>
          <h2>{editingImovel ? 'Editar Imóvel' : (adminMode ? 'Cadastrar Imóvel' : 'Anunciar Imóvel')}</h2>
          <button className={`close-btn ${adminMode ? 'admin-mode' : ''}`} onClick={onClose}>✕</button>
        </div>

        <div className="progress-bar">
          <div className="progress-steps">
            {STEPS.map((step, index) => (
              <div 
                key={step.id} 
                className={`progress-step ${currentStep >= step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
              >
                <div className="step-number">{step.id}</div>
                <div className="step-info">
                  <div className="step-title">{step.title}</div>
                  <div className="step-description">{step.description}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="progress-line">
            <div 
              className="progress-fill" 
              style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
            />
          </div>
        </div>

        <div className="form-body">
          {renderStepContent()}
        </div>

        <div className="form-footer">
          <div className="step-info">
            Etapa {currentStep} de {STEPS.length}
          </div>
          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={prevStep}
              disabled={currentStep === 1}
            >
              Voltar
            </button>
            {currentStep < STEPS.length ? (
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={nextStep}
                disabled={!isStepValid()}
              >
                Avançar
              </button>
            ) : (
              <button 
                type="button" 
                className="btn btn-success"
                onClick={() => {
                  console.log('🔘 Botão clicado!');
                  handleSubmit();
                }}
                disabled={!isStepValid() || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span> Salvando...
                  </>
                ) : (
                  editingImovel ? 'Salvar Alterações' : 'Publicar Imóvel'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
